// src/admin/pages/AccessUrlsPage.jsx
import React, { useState } from 'react';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import ShortUrlBatchCard from '../components/access-urls/ShortUrlBatchCard';
import GenerateUrlsModal from '../components/access-urls/GenerateUrlsModal';
import useShortUrlBatches from '../hooks/useShortUrlBatches';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle';
import LoadingView from '../../shared/components/LoadingView';

export default function AccessUrlsPage() {
  useDocumentTitle('Asset Access URLs – Maintella');

  const {
    batches,
    loading,
    error,
    refresh,
    generateBatch,
    exportBatchCsv,
    startBatchPdfExport,
    waitForPdfExportAndDownload,
  } = useShortUrlBatches();
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  // Handle generate new batch
  const handleGenerate = async (count, name, workflowId) => {
    await generateBatch(count, name, workflowId);
  };

  // Handle CSV export
  const handleExport = async (batchNumber) => {
    try {
      await exportBatchCsv(batchNumber);
    } catch (err) {
      alert(err.message || 'Failed to export CSV');
    }
  };

  const handleExportBatchPdf = async (batchNumber) => {
    try {
      const created = await startBatchPdfExport(batchNumber);
      await waitForPdfExportAndDownload(created.id);
    } catch (err) {
      if (err?.name === 'AbortError') return;
      alert(err.message || 'Failed to generate batch PDF');
      throw err;
    }
  };

  return (
    <>
      <ObjectTopBar
        title="Asset Access URLs"
        primaryBtn={{
          btnText: 'Generate new access URLs',
          btnIcon: 'bi-plus-lg',
          onClick: () => setShowGenerateModal(true),
        }}
      />

      <div className="container py-4">
        {/* Description */}
        <div className="mb-4">
          <h6 className="text-muted fw-normal mb-1">Access URL Management</h6>
          <p className="text-muted small mb-0">
            Manage short URLs for asset access by batch. Generate URLs, export CSV, download single-label
            or collated batch PDFs for printing, and claim URLs to associate with specific assets.
          </p>
        </div>

        {/* Loading state */}
        {loading && batches.length === 0 ? (
          <LoadingView
            symbolClass="bi-qr-code"
            title="Loading access URL batches"
            subtitle="Fetching short URL batches for your organization."
            className="py-5"
          />
        ) : null}

        {/* Error state */}
        {error && !loading && (
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle me-2" />
            Failed to load batches. Please try refreshing the page.
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && batches.length === 0 && (
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="bi bi-qr-code display-1 text-muted opacity-50 mb-3 d-block" />
              <h5 className="text-muted">No access URL batches yet</h5>
              <p className="text-muted mb-4">
                Generate your first batch of access URLs to get started. These can be printed as QR codes
                and attached to assets for easy access.
              </p>
              <button
                className="btn btn-primary"
                onClick={() => setShowGenerateModal(true)}
              >
                <i className="bi bi-plus-lg me-2" />
                Generate new access URLs
              </button>
            </div>
          </div>
        )}

        {/* Batch list */}
        {!loading && !error && batches.length > 0 && (
          <div>
            {batches.map(batch => (
              <ShortUrlBatchCard
                key={batch.id}
                batch={batch}
                onExport={handleExport}
                onExportBatchPdf={handleExportBatchPdf}
                onBatchCountsChange={refresh}
              />
            ))}
          </div>
        )}
      </div>

      {/* Generate Modal */}
      <GenerateUrlsModal
        show={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onGenerate={handleGenerate}
      />
    </>
  );
}
